import {
  authorizationSelector,
  useOrganizationIdSelector,
} from 'modules/authorization';
import { useFeatureFlag } from 'modules/feature-flags';
import { useFunction } from 'modules/firebase';
import { subscriptionSelectors } from 'modules/subscription';
import { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useRecoilValue } from 'recoil';
import { toast } from 'sonner';
import {
  buildProposalSegmentAIContext,
  createSlateContentFromGeneratedRichContent,
  extractPlainTextFromSlateContent,
} from '../utils';

interface GenerationSnapshot {
  prompt: string;
  content: string;
  serviceName: string;
  unitName: string;
  quantity: number;
}

function normalizeValue(value?: string | null) {
  return (value || '').trim();
}

export function useServiceSegmentAI(index: number) {
  const { control, setValue } =
    useFormContext<PublishStatusAware<Proposal>>();
  const { formatMessage, locale } = useIntl();
  const organizationId = useOrganizationIdSelector();
  const isTierOne = useRecoilValue(subscriptionSelectors.isTierOne);
  const isAIProposalGenerationEnabled = useFeatureFlag(
    'AI_PROPOSAL_GENERATION',
  );
  const generateServiceSuggestion = useFunction<
    GenerateServiceSuggestionData,
    GenerateServiceSuggestionPayload
  >('generateServiceSuggestion');

  const proposalName =
    useWatch({
      control,
      name: 'name',
    }) || '';
  const currentContent =
    useWatch({
      control,
      name: `segments.${index}.content`,
    }) || '';
  const currentService =
    useWatch({
      control,
      name: `segments.${index}.service`,
    }) || null;
  const currentUnit =
    useWatch({
      control,
      name: `segments.${index}.unit`,
    }) || null;
  const currentQuantity =
    useWatch({
      control,
      name: `segments.${index}.quantity`,
    }) || 0;
  const segments =
    useWatch({
      control,
      name: 'segments',
    }) || [];

  const [isOpen, setIsOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [generated, setGenerated] =
    useState<GenerateServiceSuggestionPayload | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationSnapshot, setGenerationSnapshot] =
    useState<GenerationSnapshot | null>(null);

  const { adminRole } = useSelector(authorizationSelector.getState);

  const shouldRender =
    !!organizationId &&
    isAIProposalGenerationEnabled &&
    isTierOne &&
    adminRole === 'superAdmin';
  const proposalContext = useMemo(
    () => buildProposalSegmentAIContext(segments),
    [segments],
  );
  const currentDescriptionSummary = useMemo(
    () => extractPlainTextFromSlateContent(currentContent, 700),
    [currentContent],
  );
  const currentInputSnapshot = useMemo(
    () => ({
      prompt: normalizeValue(prompt),
      content: currentContent,
      serviceName: normalizeValue(currentService?.name),
      unitName: normalizeValue(currentUnit?.name),
      quantity:
        typeof currentQuantity === 'number' && Number.isFinite(currentQuantity)
          ? currentQuantity
          : 0,
    }),
    [currentContent, currentQuantity, currentService?.name, currentUnit?.name, prompt],
  );
  const isGeneratedStale =
    !!generated &&
    !!generationSnapshot &&
    (generationSnapshot.prompt !== currentInputSnapshot.prompt ||
      generationSnapshot.content !== currentInputSnapshot.content ||
      generationSnapshot.serviceName !== currentInputSnapshot.serviceName ||
      generationSnapshot.unitName !== currentInputSnapshot.unitName ||
      generationSnapshot.quantity !== currentInputSnapshot.quantity);

  function handleOpen() {
    setIsOpen(true);
  }

  function resetState() {
    setPrompt('');
    setGenerated(null);
    setGenerationSnapshot(null);
    setIsOpen(false);
  }

  async function handleGenerate() {
    if (!organizationId) return;

    try {
      setIsGenerating(true);

      const { data } = await generateServiceSuggestion({
        organizationId,
        locale,
        proposalName: normalizeValue(proposalName) || null,
        targetIndex: index,
        currentService: {
          serviceName: normalizeValue(currentService?.name),
          unitName: normalizeValue(currentUnit?.name),
          quantity:
            typeof currentQuantity === 'number' && Number.isFinite(currentQuantity)
              ? currentQuantity
              : 0,
          description: currentDescriptionSummary,
        },
        prompt: normalizeValue(prompt) || null,
        proposalContext,
      });

      if (!data.success) {
        toast.error(
          data.message ||
            formatMessage({ id: 'proposal-ai.service.toast.generate-error' }),
        );
        return;
      }

      if (!data.payload) {
        toast.error(
          formatMessage({ id: 'proposal-ai.service.toast.generate-error' }),
        );
        return;
      }

      setGenerated(data.payload);
      setGenerationSnapshot(currentInputSnapshot);
    } catch {
      toast.error(
        formatMessage({ id: 'proposal-ai.service.toast.generate-error' }),
      );
    } finally {
      setIsGenerating(false);
    }
  }

  function handleApply() {
    if (!generated || isGeneratedStale) {
      return;
    }

    setValue(
      `segments.${index}.content`,
      createSlateContentFromGeneratedRichContent(generated.description),
      {
        shouldDirty: true,
      },
    );

    toast.success(
      formatMessage({ id: 'proposal-ai.service.toast.apply-success' }),
    );
    resetState();
  }

  return {
    generated,
    handleApply,
    handleGenerate,
    handleOpen,
    isGeneratedStale,
    isGenerating,
    isOpen,
    prompt,
    resetState,
    setPrompt,
    shouldRender,
  };
}
